import React, { useEffect, useMemo, useState } from "react";
import { useApi } from "../../context/ApiContext";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const RT_STORAGE_KEY = "roomTypes";
function loadRoomTypesLocal() {
  try {
    const raw = localStorage.getItem(RT_STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export default function UnitForm({ mode = "create" }) {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const id = params.id || searchParams.get("id") || null;
  const initialSlug = searchParams.get("slug") || "";

  const {
    adminListProperties: fetchProperties,
    adminListUnits: fetchUnits,
    adminCreateUnit,
    adminListRoomTypes,
  } = useApi();

  const [properties, setProperties] = useState([]);
  const [propertySlug, setPropertySlug] = useState(initialSlug);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [roomTypes, setRoomTypes] = useState([]);

  const [form, setForm] = useState({
    name: "",
    floor: "",
    beds: 1,
    baths: 1,
    sqft: 20,
    price: 0,
    available: true,
    roomTypeId: "",
    roomTypeName: "",
  });

  const isEdit = mode === "edit" && !!id;

  useEffect(() => {
    (async () => {
      try {
        const list = await fetchProperties();
        setProperties(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [fetchProperties]);

  // Load room types from API with fallback to any local copy
  useEffect(() => {
    (async () => {
      try {
        const list = await adminListRoomTypes();
        if (Array.isArray(list)) {
          setRoomTypes(list);
          return;
        }
      } catch (e) {
        // ignore, fallback to local
      }
      const local = loadRoomTypesLocal();
      setRoomTypes(Array.isArray(local) ? local : []);
    })();
  }, [adminListRoomTypes]);

  useEffect(() => {
    if (!isEdit) return;
    if (!propertySlug || !id) return;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const arr = await fetchUnits(propertySlug);
        const u = (arr || []).find((x) => x.id === id);
        if (!u) {
          setError("Unit not found for this property");
          return;
        }
        setForm({
          name: u.name || "",
          floor: Number(u.floor || 0),
          beds: Number(u.beds || 0),
          baths: Number(u.baths || 0),
          sqft: Number(u.sqft || 0),
          price: Number(u.price || 0),
          available: !!u.available,
          roomTypeId: u.roomTypeId || "",
          roomTypeName: u.roomTypeName || "",
        });
      } catch (e) {
        setError(e?.message || "Failed to load unit");
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, propertySlug, id, fetchUnits]);

  const selectedRoomType = useMemo(
    () => roomTypes.find((r) => r.id === form.roomTypeId),
    [form.roomTypeId, roomTypes]
  );

  function prefillFromRoomType(rt) {
    if (!rt) return;
    setForm((f) => ({
      ...f,
      roomTypeId: rt.id,
      roomTypeName: rt.name,
      beds: rt.beds ?? f.beds,
      baths: rt.baths ?? f.baths,
      sqft: rt.sqft ?? f.sqft,
      price: rt.basePrice ?? f.price,
      name: f.name || `${rt.name}`,
    }));
  }

  function onChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === "checkbox" ? !!checked : name === "price" || name === "beds" || name === "baths" || name === "sqft" ? Number(value || 0) : value,
    }));
  }

  async function onSave(e) {
    e.preventDefault();
    if (!propertySlug) return alert("Please select a property");
    try {
      setLoading(true);
      setError("");
      const payload = { ...form };
      if (isEdit) payload.id = id; // upsert behavior on API
      await adminCreateUnit(propertySlug, payload);
      navigate(`/admin/units?slug=${encodeURIComponent(propertySlug)}`);
    } catch (e) {
      setError(e?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{isEdit ? "Edit Unit" : "New Unit"}</h1>
        <button onClick={() => navigate(`/admin/units${propertySlug ? `?slug=${encodeURIComponent(propertySlug)}` : ""}`)} className="px-3 py-2 rounded-md border">Back</button>
      </div>

      <div className="bg-white border rounded-lg p-4 max-w-3xl">
        {error && <div className="text-red-600 mb-3">{error}</div>}
        <form onSubmit={onSave} className="space-y-3">
          <Field label="Property">
            <select
              className="w-full border rounded-md px-3 py-2"
              value={propertySlug}
              onChange={(e) => setPropertySlug(e.target.value)}
            >
              <option value="">Select Property…</option>
              {properties.map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.name} ({p.location})
                </option>
              ))}
            </select>
          </Field>

          <Field label="Room Type">
            <select
              name="roomTypeId"
              value={form.roomTypeId}
              onChange={(e) => {
                const rid = e.target.value;
                const rt = roomTypes.find((r) => r.id === rid);
                prefillFromRoomType(rt);
              }}
              className="w-full border rounded-md px-3 py-2"
            >
              <option value="">None</option>
              {roomTypes.map((rt) => (
                <option key={rt.id} value={rt.id}>
                  {rt.name} • {rt.beds} bed • R{Number(rt.basePrice || 0).toLocaleString()}
                </option>
              ))}
            </select>
          </Field>
          {selectedRoomType && Array.isArray(selectedRoomType.charges) && selectedRoomType.charges.length > 0 && (
            <div className="text-xs text-gray-600 -mt-2">
              Charges: {selectedRoomType.charges.map((c) => `${c.name}: R${Number(c.amount||0).toLocaleString()}`).join(", ")}
            </div>
          )}

          <Field label="Name">
            <input name="name" value={form.name} onChange={onChange} className="w-full border rounded-md px-3 py-2" required />
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Beds">
              <select name="beds" value={form.beds} onChange={onChange} className="w-full border rounded-md px-3 py-2">
                {Array.from({ length: 8 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </Field>
            <Field label="Baths">
              <input type="number" name="baths" value={form.baths} onChange={onChange} className="w-full border rounded-md px-3 py-2" />
            </Field>
            <Field label="Floor">
              <input type="text" name="floor" value={form.floor} onChange={onChange} placeholder="e.g. G, 1, 2A, B1" className="w-full border rounded-md px-3 py-2" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Size (sqm)">
              <input type="number" name="sqft" value={form.sqft} onChange={onChange} className="w-full border rounded-md px-3 py-2" />
            </Field>
            <Field label="Price (ZAR)">
              <input type="number" name="price" value={form.price} onChange={onChange} className="w-full border rounded-md px-3 py-2" />
            </Field>
          </div>

          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" name="available" checked={form.available} onChange={onChange} />
            Available
          </label>

          <div className="flex gap-2">
            <button type="submit" disabled={loading} className="px-3 py-2 rounded-md bg-black text-white">
              {isEdit ? "Update" : "Create"}
            </button>
            <button type="button" onClick={() => navigate(`/admin/units${propertySlug ? `?slug=${encodeURIComponent(propertySlug)}` : ""}`)} className="px-3 py-2 rounded-md border">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}
